import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../types/redux";
import { GetApiData } from "../api/general";
import { ROUTES } from "../constants/api";
import { SetMyCardData } from "../actions/card";
import { GetMyCardResponse } from "../types/api/card";
import useErrorModal from "./useErrorModal";

interface UseMyCardData {
  response: GetMyCardResponse | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

/**
 * Loads MyCard data, preferring the cached copy in redux and falling back to
 * the API on first mount. `refetch` always hits the network (used after the
 * card is locked/unlocked) and refreshes the redux cache.
 */
const useMyCardData = (): UseMyCardData => {
  const dispatch = useDispatch();
  const openErrorModal = useErrorModal();
  const myCardData = useSelector((state: RootState) => state.card.myCardData);

  const [response, setResponse] = useState<GetMyCardResponse | null>(myCardData ?? null);
  const [loading, setLoading] = useState<boolean>(!myCardData);

  const refetch = useCallback(async () => {
    try {
      const data = await GetApiData<GetMyCardResponse>(ROUTES.CARD.GET_MY_CARD);
      setResponse(data);
      dispatch(SetMyCardData(data));
    } catch (err) {
      openErrorModal(err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, openErrorModal]);

  useEffect(() => {
    // Cached redux data is used as the initial value, so only fetch when absent.
    if (!myCardData) {
      refetch();
    }
  }, []);

  return { response, loading, refetch };
};

export default useMyCardData;